import { queryReviewDestination } from "./queryReviewDestination";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  closeInspectionChecklistQuery,
  downloadInspectionDocument,
  getInspectionChecklist,
  getInspectionChecklistQueries,
  getMyInspectionReviewTasks,
  respondToInspectionChecklistQuery,
  uploadInspectionChecklistQueryAttachment,
} from "../../helpers/backend_helper";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import WorkflowResponseDrawer from "../../components/Inspections/WorkflowResponseDrawer";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import "./ReviewTasks.scss";
import { resolveQueryNotificationTask } from "./queryNotificationTask";
import { postData } from "../../helpers/url-request";
import { queryWorkStatus, consolidateChecklistReviews, needsAction, isWaiting, priorityReason, actionLabel } from "./workPriorities";
import { workVersion, newestWorkFirst } from "./workItemAttention";

const day = (value) => (value ? new Date(value).setHours(0, 0, 0, 0) : null);

const typeOf = (task) =>
  ["QueryResponse", "QueryResponseReview", "QueryResponseApproval"].includes(task.taskType)
    ? "query"
    : task.taskType === "FindingResponse"
    ? "finding"
    : /update/i.test(task.taskType || "")
    ? "update"
    : "review";
const labels = {
  review: "Review",
  update: "Update",
  query: "Query",
  finding: "Finding",
};
const icons = {
  all: "bx-grid-alt",
  review: "bx-check-square",
  update: "bx-file",
  query: "bx-message-rounded",
  finding: "bx-flag",
};

const statusOf = (value) =>
  value === "active"
    ? "In Progress"
    : value === "completed"
    ? "Completed"
    : value === "pending"
    ? "Pending"
    : value === "changes requested" ? "Changes requested"
    : value === "awaiting review" ? "Awaiting review"
    : value || "Pending";

const statusRank = (task) => {
  const status = String(queryWorkStatus(task) || statusOf(String(task.workflowStatus || "Pending").toLowerCase())).toLowerCase();
  return ({ pending: 0, "changes requested": 1, "in progress": 2, "awaiting review": 3, completed: 5 })[status] ?? 4;
};

const openTask = needsAction;

const isDue = (task, today, end) =>
  openTask(task) &&
  day(task.dueDate) !== null &&
  day(task.dueDate) >= today &&
  day(task.dueDate) < end;

const isOverdue = (task, today) =>
  openTask(task) && day(task.dueDate) !== null && day(task.dueDate) < today;

export default function ReviewTasks() {
  const navigate = useNavigate();
  const location = useLocation();
  const openedQueryLink = useRef("");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quick, setQuick] = useState("action");
  const empty = { search: "", type: "", cycle: "", status: "" };
  const [filters, setFilters] = useState(empty);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState({ items: [] });
  const [columnSort, setColumnSort] = useState([]);
  const [openingTaskId, setOpeningTaskId] = useState(null);
  const [workflowResponse, setWorkflowResponse] = useState(null);
  const [workflowResponseText, setWorkflowResponseText] = useState("");
  const [workflowResponseAttachments, setWorkflowResponseAttachments] =
    useState([]);
  const [uploadingResponseAttachment, setUploadingResponseAttachment] =
    useState(false);
  const [savingWorkflowResponse, setSavingWorkflowResponse] = useState(false);
  const [workflowResponseProgress, setWorkflowResponseProgress] =
    useState(null);
  const today = new Date().setHours(0, 0, 0, 0);
  const weekEnd = today + 7 * 86400000;

  const loadTasks = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getMyInspectionReviewTasks();
      if (!response.status)
        throw new Error(response.message || "Unable to load your work.");
      setTasks(response.data || []);
      window.dispatchEvent(new Event("piers:readiness-changed"));
    } catch (reason) {
      setError(reason.message || "Unable to load your work.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadTasks();
    const refresh = () => { if (document.visibilityState !== "hidden") loadTasks(); };
    window.addEventListener("focus", refresh);
    const interval = window.setInterval(refresh, 60000);
    return () => { window.removeEventListener("focus", refresh); window.clearInterval(interval); };
  }, []);

  const workItems = useMemo(() => consolidateChecklistReviews(tasks), [tasks]);

  const counts = useMemo(
    () => ({
      all: workItems.length,
      action: workItems.filter(needsAction).length,
      waiting: workItems.filter(isWaiting).length,
      completed: workItems.filter((task) => String(task.workflowStatus).toLowerCase() === "completed").length,
      unread: workItems.filter((task) => task.isUnread).length,
      review: workItems.filter(
        (task) => typeOf(task) === "review" && openTask(task)
      ).length,
      update: workItems.filter(
        (task) => typeOf(task) === "update" && openTask(task)
      ).length,
      query: workItems.filter(
        (task) => typeOf(task) === "query" && openTask(task)
      ).length,
      finding: workItems.filter(
        (task) => typeOf(task) === "finding" && openTask(task)
      ).length,
      week: workItems.filter((task) => isDue(task, today, weekEnd)).length,
      overdue: workItems.filter((task) => isOverdue(task, today)).length,
    }),
    [workItems, today, weekEnd]
  );
  const cycles = useMemo(
    () => [
      ...new Map(
        workItems
          .filter((task) => task.inspectionCycleId)
          .map((task) => [
            String(task.inspectionCycleId),
            task.inspectionCycleName || `Cycle ${task.inspectionCycleId}`,
          ])
      ).entries(),
    ],
    [workItems]
  );
  const visible = useMemo(
    () =>
      workItems
        .filter((task) => {
          const type = typeOf(task);
          if (quick === "action" && !needsAction(task)) return false;
          if (quick === "waiting" && !isWaiting(task)) return false;
          if (quick === "completed" && String(task.workflowStatus).toLowerCase() !== "completed") return false;
          if (quick === "unread" && !task.isUnread) return false;
          if (labels[quick] && (type !== quick || !openTask(task)))
            return false;
          if (quick === "week" && !isDue(task, today, weekEnd)) return false;
          if (quick === "overdue" && !isOverdue(task, today)) return false;
          if (filters.type && type !== filters.type) return false;
          if (filters.cycle && String(task.inspectionCycleId) !== filters.cycle)
            return false;
          if (
            filters.status &&
            String(queryWorkStatus(task) || statusOf(String(task.workflowStatus || "Pending").toLowerCase())).toLowerCase() !==
              statusOf(filters.status).toLowerCase()
          )
            return false;
          const search = filters.search.trim().toLowerCase();
          return (
            !search ||
            [
              task.taskName,
              task.clientName,
              task.engagementName,
              task.inspectionName,
              task.inspectionNo,
              task.inspectionCycleName,
            ].some((value) =>
              String(value || "")
                .toLowerCase()
                .includes(search)
            )
          );
        })
        .sort((a, b) => statusRank(a) - statusRank(b) || newestWorkFirst(a, b)),
    [workItems, quick, filters, today, weekEnd]
  );
  const selectQuick = (value) => {
    if (value === "all") {
      setFilters(empty);
    }
    setQuick(value);
    setPage(1);
  };

  const updateFilter = (update) => {
    setFilters(update);
    setPage(1);
  };

  const clear = () => {
    setColumnFilters({ items: [] });
    setFilters(empty);
    setPage(1);
  };

  const markRead = async (task) => {
    const ids = task.workflowTaskIds || [Number(task.id)];
    const originals = tasks.filter((item) => ids.includes(Number(item.id)));
    if (!originals.some((item) => item.isUnread)) return;
    try {
      const result = await postData("/inspection/review-tasks/read", originals.map((item) => ({ id: Number(item.id), version: workVersion(item) })));
      if (!result.status) throw new Error("Unable to mark work as read.");
      setTasks((current) => current.map((item) => originals.some((seen) => seen.id === item.id && workVersion(seen) === workVersion(item)) ? { ...item, isUnread: false } : item));
    } catch (reason) {
      ErrorNotify(reason.message || "Unable to mark work as read.");
    }
  };
  const open = async (task) => {
    if (task.isConsolidatedReview) {
      await markRead(task);
      navigate(
        `/inspectionreview/${task.inspectionId}/checklist?assignedReview=1`,
        { state: { fromPath: "/review-tasks", fromLabel: "My Work" } }
      );
      return;
    }
    if (typeOf(task) === "query") {
      if (openingTaskId) return;
      setOpeningTaskId(task.id);
      try {
        const [queryResponse, checklistResponse] = await Promise.all([
          getInspectionChecklistQueries(task.inspectionId),
          getInspectionChecklist(task.inspectionId),
        ]);
        if (!queryResponse?.status)
          throw new Error(
            queryResponse?.message || "Unable to load the query."
          );
        const query = (queryResponse.data || []).find(
          (item) => Number(item.queryId || item.id) === Number(task.queryId)
        );
        if (!query)
          throw new Error("The related query is no longer available.");
        const question = (
          checklistResponse?.status && Array.isArray(checklistResponse.data)
            ? checklistResponse.data
            : []
        ).find(
          (item) => Number(item.id) === Number(query.inspectionChecklistId)
        );
        const destination = queryReviewDestination(task, query, question);
        await markRead(task);
        navigate(destination, { state: { fromPath: "/review-tasks", fromLabel: "My Work" } });
      } catch (reason) {
        ErrorNotify(reason.message || "Unable to open the query response.");
      } finally {
        setOpeningTaskId(null);
      }
      return;
    }
    await markRead(task);
    navigate(
      `/inspectionreview/${task.inspectionId}?workflowTaskId=${task.id}`
    );
  };

  useEffect(() => {
    const notificationKey = `${location.key}:${location.search}`;
    if (loading || openedQueryLink.current === notificationKey) return;
    const params = new URLSearchParams(location.search);
    const inspectionId = Number(params.get("inspectionId"));
    const queryId = Number(params.get("queryId"));
    if (!inspectionId || !queryId) return;
    openedQueryLink.current = notificationKey;
    resolveQueryNotificationTask(tasks, inspectionId, queryId, getMyInspectionReviewTasks)
      .then(({ task, refreshedTasks }) => {
        if (refreshedTasks) setTasks(refreshedTasks);
        return open(tasks.find((item) => String(item.id) === params.get("taskId") && Number(item.inspectionId) === inspectionId && Number(item.queryId) === queryId) || task);
      })
      .catch((reason) => ErrorNotify(reason.message || "Unable to open the query review."));
    // Open a notification once; subsequent task refreshes must not reopen it.
  }, [loading, location.search, location.key]);

  const closeWorkflowResponse = () => {
    if (savingWorkflowResponse || uploadingResponseAttachment) return;
    setWorkflowResponse(null);
    setWorkflowResponseText("");
    setWorkflowResponseAttachments([]);
    setWorkflowResponseProgress(null);
  };

  const uploadWorkflowResponseAttachment = (file) => {
    if (
      !file ||
      !workflowResponse ||
      uploadingResponseAttachment ||
      savingWorkflowResponse
    )
      return;
    setWorkflowResponseAttachments((current) =>
      current.some((item) => item.name === file.name && item.size === file.size)
        ? current
        : [...current, file]
    );
  };

  const downloadWorkflowResponseAttachment = async (file) => {
    try {
      await downloadInspectionDocument(
        workflowResponse.task.inspectionId,
        file.id,
        file.fileName || file.name
      );
    } catch (reason) {
      ErrorNotify(reason.message || "Unable to download the query attachment.");
    }
  };

  const saveWorkflowResponse = async () => {
    const responseText = workflowResponseText.trim();
    if (
      !workflowResponse ||
      !responseText ||
      savingWorkflowResponse ||
      uploadingResponseAttachment
    )
      return;
    setSavingWorkflowResponse(true);
    try {
      const queryId = workflowResponse.item.queryId || workflowResponse.item.id;
      const pendingAttachments = [...workflowResponseAttachments];
      if (pendingAttachments.length) {
        setUploadingResponseAttachment(true);
        setWorkflowResponseProgress({
          label: `Uploading attachments (0 of ${pendingAttachments.length})…`,
          value: 5,
        });
      } else {
        setWorkflowResponseProgress({ label: "Submitting reply…", value: 85 });
      }

      for (let index = 0; index < pendingAttachments.length; index += 1) {
        const file = pendingAttachments[index];
        try {
          const formData = new FormData();
          formData.append("attachmentType", "Response");
          formData.append("document", file);
          const uploadResponse = await uploadInspectionChecklistQueryAttachment(
            workflowResponse.task.inspectionId,
            queryId,
            formData
          );
          if (!uploadResponse?.status)
            throw new Error(
              uploadResponse?.message ||
                "Unable to upload the query response attachment."
            );
          setWorkflowResponseAttachments((items) =>
            items.filter((item) => item !== file)
          );
          setWorkflowResponse((current) =>
            current
              ? {
                  ...current,
                  item: {
                    ...current.item,
                    attachments: [
                      ...(current.item.attachments || []),
                      uploadResponse.data,
                    ],
                  },
                }
              : current
          );
        } catch (reason) {
          setWorkflowResponseProgress({
            label: `Upload failed: ${file.name}`,
            value: 5 + (index / pendingAttachments.length) * 70,
          });
          throw new Error(
            `${file.name} could not be uploaded. ${
              reason.message || "Please check the file and try again."
            }`
          );
        }
        const completed = index + 1;
        setWorkflowResponseProgress({
          label: `Uploading attachments (${completed} of ${pendingAttachments.length})…`,
          value: 5 + (completed / pendingAttachments.length) * 70,
        });
      }

      setUploadingResponseAttachment(false);
      setWorkflowResponseProgress({ label: "Submitting reply…", value: 85 });
      const response = await respondToInspectionChecklistQuery(
        workflowResponse.task.inspectionId,
        queryId,
        { response: responseText }
      );
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to submit the query response."
        );
      setWorkflowResponseProgress({ label: "Response submitted", value: 100 });
      SuccessNotify(
        pendingAttachments.length
          ? "Query response and attachments submitted."
          : "Query response submitted."
      );
      setWorkflowResponse(null);
      setWorkflowResponseText("");
      setWorkflowResponseAttachments([]);
      setWorkflowResponseProgress(null);
      await loadTasks();
    } catch (reason) {
      ErrorNotify(reason.message || "Unable to submit the query response.");
    } finally {
      setUploadingResponseAttachment(false);
      setSavingWorkflowResponse(false);
    }
  };

  const resolveWorkflowResponse = async () => {
    if (
      !workflowResponse ||
      savingWorkflowResponse ||
      uploadingResponseAttachment
    )
      return;
    setSavingWorkflowResponse(true);
    try {
      const queryId = workflowResponse.item.queryId || workflowResponse.item.id;
      const response = await closeInspectionChecklistQuery(
        workflowResponse.task.inspectionId,
        queryId
      );
      if (!response?.status)
        throw new Error(response?.message || "Unable to resolve the query.");
      SuccessNotify("Query resolved.");
      setWorkflowResponse(null);
      setWorkflowResponseText("");
      setWorkflowResponseAttachments([]);
      await loadTasks();
    } catch (reason) {
      ErrorNotify(reason.message || "Unable to resolve the query.");
    } finally {
      setSavingWorkflowResponse(false);
    }
  };

  const action = (task) => needsAction(task) ? actionLabel(task) : "View";
  const cards = [
    ["all", "All Work", "All assigned items"],
    ["review", "Assigned Reviews", "Reviews to complete"],
    ["update", "Inspection Updates", "Awaiting your updates"],
    ["query", "Open Queries", "Need response"],
    ["finding", "Open Findings", "Need review / approval"],
    ["week", "Due This Week", "Across all items"],
  ];
  const quickItems = [
    ["action", "Needs your action", "bx-check-square"],
    ["waiting", "Waiting on others", "bx-time-five"],
    ["completed", "Completed", "bx-check-circle"],
    ["all", "All", ""],
    ["unread", "Unread", "bx-envelope"],
    ["review", "My Reviews", "bx-user"],
    ["update", "Inspection Updates", icons.update],
    ["query", "Queries", icons.query],
    ["finding", "Findings", icons.finding],
    ["week", "Due This Week", "bx-calendar"],
    ["overdue", "Overdue", "bx-error"],
  ];

  const renderCells = (task) => {
    const type = typeOf(task),
      status = String(task.workflowStatus || "Pending").toLowerCase();
    const updated = task.updatedOn || task.createdOn;
    const updatedDate = updated ? new Date(updated) : null;
    const hasUpdatedDate = updatedDate && Number.isFinite(updatedDate.getTime());
    return (
      <>
        <div className="task-list-cell">
          <button
            type="button"
            className="task-list-link"
            onClick={() => open(task)}
          >
            {task.isUnread && <span className="work-unread-dot" role="img" aria-label="Unread" title="Unread" />}
            {task.taskName || labels[type]}
          </button>
          {needsAction(task) && <small>{priorityReason(task)}</small>}
          <small>
            {task.isConsolidatedReview
              ? `${task.assignedRole || "Reviewer"} · ${
                  task.checklistCount
                } checklist${task.checklistCount === 1 ? "" : "s"} assigned`
              : task.assignedRole || task.inspectionName || "Inspection review"}
          </small>
        </div>
        <div className="task-list-cell">
          <span className={`task-list-type task-list-type--${type}`}>
            <i className={`bx ${icons[type]}`} />
            {labels[type]}
          </span>
        </div>
        <div className="task-list-cell">
          {task.clientName || task.engagementName || "—"}
          <small>
            {task.clientName ? task.engagementName : task.inspectionCycleName}
          </small>
        </div>
        <div className="task-list-cell">{task.inspectionNo || task.inspectionName || "—"}</div>
        <div className="task-list-cell">
          <span className={`task-list-status task-list-status--${status}`}>
            {queryWorkStatus(task) || statusOf(status)}
          </span>
        </div>
        <div className="task-list-cell task-list-updated">
          {hasUpdatedDate ? <time dateTime={updatedDate.toISOString()} title={updatedDate.toLocaleString()}>
            {updatedDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
            <small>{updatedDate.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZoneName: "short" })}</small>
          </time> : "—"}
        </div>
        <div className="task-list-cell">
          <button
            type="button"
            className={`review-task-action review-task-action--${
              status === "active" ? "primary" : "secondary"
            }`}
            onClick={() => open(task)}
            disabled={openingTaskId === task.id}
          >
            {openingTaskId === task.id ? "Opening…" : action(task)}
          </button>
        </div>
      </>
    );
  };

  const columns = useMemo(() => [
    { id: "task", header: "Task / Item", accessorFn: (task) => task.taskName || labels[typeOf(task)], flex: 3, minSize: 240 },
    { id: "type", header: "Type", accessorFn: (task) => labels[typeOf(task)], flex: 0.8, minSize: 110 },
    { id: "client", header: "Client / Engagement", accessorFn: (task) => [task.clientName, task.engagementName].filter(Boolean).join(" / "), flex: 2, minSize: 180 },
    { id: "inspection", header: "Inspection", accessorFn: (task) => task.inspectionNo || task.inspectionName || "—", flex: 1.2, minSize: 130 },
    { id: "status", header: "Status", accessorFn: (task) => queryWorkStatus(task) || statusOf(String(task.workflowStatus || "Pending").toLowerCase()), flex: 1, minSize: 150 },
    { id: "updated", header: "Last Updated", accessorFn: (task) => workVersion(task), flex: 1.4, minSize: 180 },
    { id: "action", header: "Action", enableSorting: false, enableColumnFilter: false, flex: 1, minSize: 145 },
  ].map((column, index) => ({
    ...column,
    cell: ({ row }) => renderCells(row.original).props.children[index],
  })), [tasks, openingTaskId]);

  return (
    <main className="page-content task-list-page piers-page">
      <header className="task-list-page__header piers-page-header">
        <div>
          <h1 className="piers-page-title">My Work</h1>
          <p>
            Your assigned inspection reviews, updates, queries, findings and
            tasks.
          </p>
        </div>
        <div className="task-list-page__header-actions">
          <time>
            {new Date().toLocaleDateString("en-GB", {
              weekday: "long",
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </time>
          <button
            type="button"
            className="task-list-refresh piers-refresh-button"
            onClick={loadTasks}
          >
            <i className="bx bx-refresh" /> Refresh
          </button>
        </div>
      </header>
      <section className="task-list-summary" aria-label="My work summary">
        {cards.map(([id, label, note]) => (
          <button
            key={id}
            type="button"
            className={`task-list-summary__card task-list-summary__card--${id}`}
            onClick={() => selectQuick(id)}
            aria-pressed={quick === id}
          >
            <i className={`bx ${icons[id] || "bx-time-five"}`} />
            <span>
              <b>{label}</b>
              <strong>{counts[id]}</strong>
              <small>{note}</small>
            </span>
            <i className="bx bx-chevron-right task-list-summary__chevron" />
          </button>
        ))}
      </section>
      {/* Temporarily hidden while summary cards provide the primary shortcuts. */}
      <section
        className="task-list-quick-filters"
        aria-label="Quick filters"
        hidden
        style={{ display: "none" }}
      >
        <strong>Quick Filters:</strong>
        {quickItems.map(([id, label, icon]) => (
          <button
            key={id}
            type="button"
            className={quick === id ? "active" : ""}
            onClick={() => selectQuick(id)}
            aria-pressed={quick === id}
          >
            {icon && <i className={`bx ${icon}`} />}
            {label}
            <b>{counts[id]}</b>
          </button>
        ))}
      </section>
      <section
        className="task-list-grid"
        data-onboarding-target="assignment"
        tabIndex="-1"
      >
        <div className="task-list-grid__header">
          <div className="task-list-results-title">
            <h2>My Work ({visible.length})</h2>
            <div className="task-list-results-guidance">
              <p>Open a task or item name to view its details and next steps.</p>
              {(quick !== "all" || Object.values(filters).some(Boolean)) && (
                <button type="button" className="task-list-results-reset" onClick={() => selectQuick("all")}>Show all work</button>
              )}
            </div>
          </div>
          <div className="task-list-results-help" role="note" aria-label="Grid action help">
            <i className="bx bx-info-circle" aria-hidden="true" />
            <p>Use the <strong>Action</strong> button to open the item<br />and continue its review, response or update.</p>
          </div>
        </div>
        
        {error && (
          <div className="task-list-message" role="alert">
            {error}{" "}
            <button type="button" onClick={loadTasks}>
              Retry
            </button>
          </div>
        )}
          <CustomDataGrid
 filterContent={<div className="task-list-filter-panel">
          <label className="task-list-search">
            <span className="visually-hidden">Search work</span>
            <i className="bx bx-search" />
            <input
              value={filters.search}
              onChange={(event) =>
                updateFilter((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              placeholder="Search by client, engagement, inspection, or task..."
            />
          </label>
          <label>
            Work Type
            <select
              value={filters.type}
              onChange={(event) =>
                updateFilter((current) => ({ ...current, type: event.target.value }))
              }
            >
              <option value="">All Types</option>
              {Object.entries(labels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Inspection Cycle
            <select
              value={filters.cycle}
              onChange={(event) =>
                updateFilter((current) => ({ ...current, cycle: event.target.value }))
              }
            >
              <option value="">All Cycles</option>
              {cycles.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={filters.status}
              onChange={(event) =>
                updateFilter((current) => ({
                  ...current,
                  status: event.target.value,
                }))
              }
            >
              <option value="">All Statuses</option>
              {["active", "pending", "completed", "changes requested", "awaiting review"].map((value) => (
                <option key={value} value={value}>
                  {statusOf(value)}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="task-list-clear" onClick={clear}>
            Clear
          </button>
        </div>}

            loading={loading}
            emptyState={{
              title: quick === "action" ? "No items need your action right now." : "No work matches the selected filters.",
              description: "Adjust the filters or show all work to see other items.",
              filtered: true,
              onReset: () => { selectQuick("all"); clear(); },
            }}
            data={visible}
            columns={columns}
            showToolbar={false}
            recordLabel="items"
            columnHeaderHeight={56}
            getRowHeight={() => "auto"}
            getRowClassName={({ row }) => row.__original.isUnread ? "work-item-unread" : ""}
            filterModel={columnFilters}
            onFilterModelChange={(model) => { setColumnFilters(model); setPage(1); }}
            sortModel={columnSort}
            onSortModelChange={(model) => { setColumnSort(model); setPage(1); }}
            paginationModel={{ page: page - 1, pageSize }}
            onPaginationModelChange={(model) => { setPage(model.page + 1); setPageSize(model.pageSize); }}
            gridSx={{
              border: 0,
              "& .MuiDataGrid-row.work-item-unread": { backgroundColor: "#f5f8ff" },
            }}
          />
      </section>
      <WorkflowResponseDrawer
        inspectionId={workflowResponse?.task?.inspectionId}
        onReviewPosted={loadTasks}
        presentation="modal"
        response={workflowResponse}
        value={workflowResponseText}
        onChange={setWorkflowResponseText}
        onClose={closeWorkflowResponse}
        onSubmit={saveWorkflowResponse}
        onResolve={
          workflowResponse?.item?.canClose && workflowResponse?.item?.resolutionEligible &&
          !/closed|resolved/i.test(String(workflowResponse.item.status || ""))
            ? resolveWorkflowResponse
            : null
        }
        attachments={workflowResponseAttachments}
        onUpload={uploadWorkflowResponseAttachment}
        onDownload={downloadWorkflowResponseAttachment}
        uploading={uploadingResponseAttachment}
        saving={savingWorkflowResponse}
        progress={workflowResponseProgress}
      />
    </main>
  );
}
